import React, { useState, useEffect } from "react";
import {
  Box, Modal, Typography, Button, Divider, CircularProgress, useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import { getPaquetesPorOrden, liberarOrdenCarga } from "../../services/ordenCarga.services";
import { toast } from "react-toastify";
import secureStorage from "../../utils/secureStorage";

const colsPlanificado = [
  { field: "producto_descripcion", headerName: "Producto", flex: 2 },
  // { field: "espesor_mm", headerName: "Espesor (mm)", flex: 0.7, type: "number" },
  // { field: "nro_paquetes", headerName: "Paquetes", flex: 0.6, type: "number" },
  // { field: "chapas_por_paquete", headerName: "Ch/Paq", flex: 0.6, type: "number" },
  { field: "nro_chapas", headerName: "Total chapas", flex: 0.7, type: "number" },
  { field: "peso_kg", headerName: "Peso (kg)", flex: 0.7, type: "number" },
];

const colsEscaneado = [
  { field: "serie", headerName: "Serie", flex: 1.5 },
  { field: "descripcion", headerName: "Descripción", flex: 1.5 },
  { field: "cantidad", headerName: "Cantidad", flex: 0.6, type: "number" },
  { field: "n_pedido", headerName: "Pedido", flex: 0.8 },
];

const gridSx = (colors) => ({
  "& .MuiDataGrid-root": { border: "none" },
  "& .MuiDataGrid-cell": { borderBottom: "none" },
  "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[900], borderBottom: "none" },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[500] },
  "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[900] },
});

const ModalLiberarOrden = ({ open, onClose, orden, onLiberado }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [liberando, setLiberando] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const id_usuario = parseInt(secureStorage.getItem("id") || "15");
  const cod_empresa = parseInt(secureStorage.getItem("cod_empresa") || "1");

  useEffect(() => {
    if (open && orden?.id) {
      setLoading(true);
      getPaquetesPorOrden(orden.id)
        .then(setData)
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setData(null);
    }
  }, [open, orden?.id]);

  const totalPlanificado = (data?.planificado ?? []).reduce((acc, r) => acc + (Number(r.nro_chapas) || 0), 0);
  const totalEscaneado  = (data?.escaneado  ?? []).reduce((acc, r) => acc + (Number(r.cantidad)  || 0), 0);
  const match = totalPlanificado > 0 && totalEscaneado === totalPlanificado;

  const handleConfirmar = async () => {
    setLiberando(true);
    try {
      await liberarOrdenCarga(orden.id, {
        id_vehiculo: orden.id_vehiculo,
        id_usuario,
        cod_empresa,
      });
      toast.success(`Orden ${orden.nro_orden} liberada — stock actualizado`);
      onLiberado?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLiberando(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box sx={{
        backgroundColor: colors.primary[600],
        padding: "24px",
        width: "min(95vw, 900px)",
        maxHeight: "90vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        borderRadius: "4px",
      }}>
        {/* Header */}
        <Box>
          <Typography variant="h4" fontWeight="bold">Liberación de paquetes</Typography>
          {orden && (
            <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5 }}>
              {orden.nro_orden} &nbsp;|&nbsp; {orden.chapa ?? "-"} &nbsp;|&nbsp; {orden.motorista_nombre} &nbsp;|&nbsp;
              {orden.fecha_carga ? new Date(orden.fecha_carga).toLocaleDateString() : "-"}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <>
            {/* Planificado */}
            <Box>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Ítems planificados ({totalPlanificado} chapas)
              </Typography>
              <Box sx={{ height: 220, ...gridSx(colors) }}>
                <DataGrid
                  rows={data?.planificado ?? []}
                  columns={colsPlanificado}
                  getRowId={(r) => r.id}
                  hideFooter
                  disableSelectionOnClick
                />
              </Box>
            </Box>

            <Divider sx={{ borderColor: colors.primary[300] }} />

            {/* Escaneado */}
            <Box>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Series escaneadas ({totalEscaneado} chapas)
              </Typography>
              <Box sx={{ height: 240, ...gridSx(colors) }}>
                <DataGrid
                  rows={data?.escaneado ?? []}
                  columns={colsEscaneado}
                  getRowId={(r) => r.id}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25]}
                  disableSelectionOnClick
                />
              </Box>
            </Box>

            {/* Resumen */}
            <Box
              sx={{
                backgroundColor: match ? "rgba(76,175,80,0.15)" : "rgba(255,152,0,0.15)",
                border: `1px solid ${match ? "#4caf50" : "#ff9800"}`,
                borderRadius: "4px",
                padding: "10px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body1" fontWeight="bold" sx={{ color: match ? "#4caf50" : "#ff9800" }}>
                {match
                  ? `✓ Todo coincide: ${totalEscaneado} / ${totalPlanificado} chapas`
                  : `⚠ Diferencia: escaneado ${totalEscaneado} / planificado ${totalPlanificado} chapas`}
              </Typography>
            </Box>

            {/* Acciones */}
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={onClose} disabled={liberando}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirmar}
                disabled={liberando || !data?.escaneado?.length}
                sx={{ minWidth: 180 }}
              >
                {liberando ? "Liberando..." : "Confirmar liberación"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ModalLiberarOrden;
